import { useEffect, useState } from 'react';

import { fetchRegions, type RegionNode } from '../../../../api/regions';
import { editUserAccess, getUserDetail, UserMgmtError, type RoleKey } from '../../../../api/users';
import { topLevelRegionOptions, type RegionOption } from '../../../../shared/regionAccessOptions';
import { editableRegions } from '../edit-region-choices';
import type { UserRoleId } from '../roleTaxonomy';
import { canTake, type UserRow } from '../userViewModel';

import EditRoleBody from './EditRoleBody';
import EditRoleFooter from './EditRoleFooter';
import EditRoleHeader from './EditRoleHeader';
import EditRoleLoadErrorBanner from './EditRoleLoadErrorBanner';
import EditRoleLoadingBanner from './EditRoleLoadingBanner';
import EditRoleSaveErrorBanner from './EditRoleSaveErrorBanner';
import ModalScrim from './ModalScrim';
import { useAccessDraft, type AccessState } from './use-access-draft';

interface EditRoleModalProps {
  user: UserRow;
  /** True when this row IS the viewer. Admins may edit their own regions
   *  (BON-433); doing so has to re-derive the persona chrome. */
  isSelf?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/** Load the full region tree for the multi-select. */
function useRegionOptions(fallback: RegionOption[]): RegionOption[] {
  const [regions, setRegions] = useState<RegionOption[]>(fallback);
  useEffect(() => {
    let cancelled = false;
    fetchRegions()
      .then((tree: RegionNode[]) => !cancelled && setRegions(topLevelRegionOptions(tree)))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return regions;
}

interface EditChoices {
  grantableRoleIds: UserRoleId[];
  assignableRegionIds: string[];
}

const NO_CHOICES: EditChoices = { grantableRoleIds: [], assignableRegionIds: [] };

/** Fetch the user's current Auth0 roles + region set from `GET /users/:id`,
 *  along with what the viewer may change. */
function useUserDetail(userId: string): {
  data: AccessState | null;
  choices: EditChoices;
  loadError: string | null;
} {
  const [data, setData] = useState<AccessState | null>(null);
  const [choices, setChoices] = useState<EditChoices>(NO_CHOICES);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getUserDetail(userId)
      .then((d) => {
        if (cancelled) return;
        setData({ roleIds: d.roles as UserRoleId[], regionIds: d.regionIds });
        setChoices({
          grantableRoleIds: d.grantableRoles as UserRoleId[],
          assignableRegionIds: d.assignableRegionIds,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Could not load user.');
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  return { data, choices, loadError };
}

/**
 * Map a backend error code to operator-friendly modal text. Centralized in
 * a lookup so the complexity cap stays sane and so each message can be
 * audited against the spec § "Service-layer guardrails" wording.
 */
const ERROR_TEXT: Record<string, string> = {
  LAST_ADMIN_GUARD:
    "Can't remove the last active Admin. Promote another user to Admin first, then retry.",
  SELF_TARGET: "You can't change your own role or regions. Ask another Admin to make this change.",
  TARGET_INACTIVE: 'This user is deactivated. Reactivate them before editing roles or regions.',
  TARGET_HAS_NO_AUTH0: "Roles and regions are set when they're invited. Send them an invite first.",
  TARGET_OUTRANKS_CALLER: 'Only an Admin can change this user.',
  TARGET_NOT_FOUND: 'User not found. They may have been removed in another session.',
  RD_FORBIDDEN_ROLE:
    'Regional Directors can only change the Instructor role on a user. Ask an Admin to handle other role changes.',
  RD_OUT_OF_SCOPE_REGION:
    'One or more selected regions are outside your scope. Pick regions you manage.',
  RD_TARGET_OUT_OF_SCOPE: 'This user has regions outside your scope. Ask an Admin to edit them.',
  RD_NEEDS_ONE_REGION: 'A Regional Director has exactly one region. Pick a single region.',
  REGION_HAS_DIRECTOR:
    'That region already has a director. Clear them on the region page first, then retry.',
  // No longer tells the operator to split the edit — since BON-434 a combined
  // roles+regions save is validated as a whole, so this only fires when the
  // change genuinely would leave a region-scoped role with no regions.
  REGION_SCOPED_ROLE_NEEDS_REGION:
    'A Regional Director or Instructor must have at least one region. Select a region as well, or remove the role.',
};

function explainError(err: unknown): string {
  if (!(err instanceof UserMgmtError)) {
    return err instanceof Error ? err.message : 'Save failed. Please retry.';
  }
  return (err.code && ERROR_TEXT[err.code]) || err.message;
}

/** Edit a user's role(s) + region scope. Multi-role checkbox group;
 *  sequential PATCH on save (roles first so the last-admin guard can block
 *  the whole save cleanly, then regions). Handles all BON-54 error codes. */
export default function EditRoleModal({
  user,
  isSelf = false,
  onClose,
  onSaved,
}: EditRoleModalProps) {
  // The user's existing grants stand in until the full tree loads; 'covers' is
  // display-only and unknown until then.
  const allRegions = useRegionOptions(
    user.regions.map((r) => ({ ...r, covers: [], directorUserId: null })),
  );
  const { data: initial, choices, loadError } = useUserDetail(user.id);

  const { regions, lockedRegionIds } = editableRegions(
    allRegions,
    initial?.regionIds ?? [],
    choices.assignableRegionIds,
  );

  const draft = useAccessDraft(initial, regions, user.id);
  const { roleIds, regionIds, rolesChanged, regionsChanged } = draft;
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const hasChanges = rolesChanged || regionsChanged;
  const canSave = hasChanges && !submitting && initial !== null && draft.regionsFitRoles;

  async function handleSave(): Promise<void> {
    if (!initial) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      // ONE request (BON-434). As two calls, roles went first and were judged
      // against the not-yet-saved region set — so granting Instructor and a
      // first region together always 400'd, which is every new instructor.
      // Sending only what changed keeps a roles-only edit from needing
      // `manage:user_regions`, and vice versa.
      await editUserAccess(user.id, {
        ...(rolesChanged ? { roleKeys: roleIds as RoleKey[] } : {}),
        ...(regionsChanged ? { regionIds } : {}),
      });
      onSaved();
      // Editing your OWN regions changes the chrome: persona, scope label and
      // the region calendars all derive from a one-shot `/staff/me` fetch in
      // usePersona. Refetching the user LIST does not touch any of that, so
      // reload — the same thing the old My-regions modal did, and for the same
      // reason (BON-433).
      if (isSelf && regionsChanged) window.location.reload();
    } catch (err) {
      // No partial-save message any more: the server validates the whole
      // change before writing anything, so a failure means nothing landed.
      setSaveError(explainError(err));
      setSubmitting(false);
    }
  }

  return (
    <ModalScrim width={580} onClose={onClose} labelledBy="edit-role-title">
      <EditRoleHeader user={user} />
      {loadError ? (
        <EditRoleLoadErrorBanner message={loadError} />
      ) : !initial ? (
        <EditRoleLoadingBanner />
      ) : (
        <EditRoleBody
          regions={draft.regionChoices}
          selectedRoleIds={roleIds}
          selectedRegionIds={regionIds}
          lockedRegionIds={lockedRegionIds}
          grantableRoleIds={choices.grantableRoleIds}
          canEditRegions={canTake(user, 'edit-regions')}
          onRolesChange={draft.changeRoles}
          onRegionsChange={draft.changeRegions}
        />
      )}
      {saveError && <EditRoleSaveErrorBanner message={saveError} />}
      <EditRoleFooter
        rolesChanged={rolesChanged}
        regionsChanged={regionsChanged}
        submitting={submitting}
        canSave={canSave}
        onCancel={onClose}
        onSave={handleSave}
      />
    </ModalScrim>
  );
}
